import { FC } from "react";
import CurrencyIconSVG from "../../../svg/CurrencyIconSVG";
import WorkLabels from "../WorkLabels";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getNormalizedDate, getNormalizedTime } from "../../../../../tools/DatetimeTools";
import ClockIconSVG from "../../../svg/ClockIconSVG";
import ContactsPeopleIconSVG from "../../../svg/ContactsPeopleIconSVG";
import AddToSavedButton from "../../AddToSavedButton";
import { getWorkCreatedTime, getWorkDistance } from "../../../../../slices/works-slice";
import { WorkListType, WorkModel } from "../../../../../domain/models/Work";
import { WorkStatus } from "../../../../../domain/enums/WorkStatusEnum";
import CurrencyAmount from "../../CurrencyAmount";

interface WorkCardProps {
    work: WorkModel;
    type: WorkListType;
    className?: string;
}

const WorkCard: FC<WorkCardProps> = (props) => {
    const { t } = useTranslation();

    if (props.work.status === WorkStatus.DELETED) {
        return <></>;
    }

    return (
        <div className={`relative ${props.className}`}>
            <Link
                to={`/work/${props.work.id}`}
                className="relative cursor-pointer flex flex-col bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] transition duration-300 rounded-[10px] py-9 td4:py-8 tmax4:py-5 px-10 td4:px-5 tmax4:px-4 max-w-[845px]"
            >
                <div className="flex justify-between mb-8 dmax2:mb-5">
                    <WorkLabels work={props.work} />
                    <div className="H16 tmax4:H14 text-textFaded">{getWorkCreatedTime(t, props.work, true)}</div>
                </div>
                <div className="flex flex-col items-start w-full mb-8 tmax4:mb-5">
                    <p className="dmin2:H24B td4:H24B tmax4:H20B text-textWhite text-start max-w-full break-words mb-4 tmax4:mb-3">
                        {props.work.title}
                    </p>
                    <p className="dmin2:H20 td4:H20 tmax4:H14 text-textFaded text-start max-w-full break-words mb-4 tmax4:mb-3">
                        {props.work.address.country}, {props.work.address.city}, {props.work.address.address}{" "}
                        {getWorkDistance(props.work, t)}
                    </p>
                    <div className="flex items-end">
                        <span className="H20 dmax2:H16 text-textFaded">{t("work.workStart")}</span>
                        <span className="H20B dmax2:H14B text-textWhite ml-[18px] dmax2:ml-2">
                            {getNormalizedDate(props.work.startFrom)}
                        </span>
                        <span className="H20 dmax2:H14B text-textWhite ml-5 dmax2:ml-3">
                            {getNormalizedTime(props.work.startFrom)}
                        </span>
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 dmax2:gap-2 max-w-[calc(100%-32px)]">
                    <div className="H16 flex justify-center items-center dmax2:H14 text-textWhite bg-primaryBlue rounded-[30px] w-fit tmax5:h-[30px] px-4 dmax2:px-2.5 py-2 dmax2:py-[7px]">
                        <CurrencyIconSVG className="w-6 dmax2:w-4 h-6 dmax2:h4 mr-3 dmax2:mr-1.5 text-textWhite" />
                        <span>
                            <CurrencyAmount price={props.work.price} suffix={t("work.applicationCreation.priceUnits")} />
                        </span>
                    </div>
                    <div className="H16 flex justify-center items-center dmax2:H14 text-textWhite bg-primaryBlue rounded-[30px] w-fit tmax5:h-[30px] px-4 dmax2:px-2.5 py-2 dmax2:py-[7px]">
                        <ClockIconSVG className="w-6 dmax2:w-4 h-6 dmax2:h4 mr-3 dmax2:mr-1.5 text-textWhite" />
                        <span>
                            {props.work.estimatedHours} {t("work.estimatedHours")}
                        </span>
                    </div>
                    <div className="H16 flex justify-center items-center dmax2:H14 text-textWhite bg-primaryBlue rounded-[30px] w-fit tmax5:h-[30px] px-4 dmax2:px-2.5 py-2 dmax2:py-[7px]">
                        <ContactsPeopleIconSVG className="w-6 dmax2:w-4 h-6 dmax2:h4 mr-3 dmax2:mr-1.5 text-textWhite" />
                        <span>
                            {props.work.workersAmount} {t("work.workersAmount")}
                        </span>
                    </div>
                </div>
            </Link>
            <AddToSavedButton
                work={props.work}
                className="absolute z-[100] right-10 bottom-9 td4:bottom-8 tmax4:bottom-5 td4:right-5 tmax4:right-4"
            />
        </div>
    );
};

export default WorkCard;
